import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWindowSize } from 'react-use';
import { Box, Breadcrumbs, CircularProgress, Grid, Link, Typography, useTheme } from '@mui/material';
import { ArrowForwardIos } from '@mui/icons-material';

import { CrystalButtonPopup, MostActiveZones, ZonesTabInBuilding } from 'components';
import { ROUTES } from 'consts';
import { getBuildingLocation } from 'utils';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useParamValues } from 'hooks/url.hook';
import { useBuilding } from 'pages/buildings/buildings.hook';
import { BuildingDeleteDialog, BuildingUpdateDialog } from 'dialogs/building';
import { ZoneCreateDialog } from 'dialogs/zone';

interface BuildingDetailsLayoutProps {
  children?: React.ReactNode;
}

export const BuildingDetailsLayout = ({ children = null }: BuildingDetailsLayoutProps) => {
  const { orgSlug, isAdmin } = useAppSelector(selectCurrentUserInfo);
  const { buildingId } = useParamValues(['buildingId']);
  const [loading, building] = useBuilding({ buildingId: Number(buildingId) });
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const theme = useTheme();
  const [visibleBuildingUpdateDialog, setVisibleBuildingUpdateDialog] = useState<boolean>(false);
  const [visibleBuildingDeleteDialog, setVisibleBuildingDeleteDialog] = useState<boolean>(false);
  const [visibleZoneCreateDialog, setVisibleZoneCreateDialog] = useState<boolean>(false);

  useEffect(() => {
    if (!loading && !building) {
      navigate(`/${orgSlug}/${ROUTES.BUILDINGS}`);
    }
  }, [loading, building]);

  const pageMenuItems = [
    {
      id: 1,
      label: 'New',
      onClick: () => {
        navigate(`/${orgSlug}/${ROUTES.BUILDING_CREATE}`);
      },
    },
    {
      id: 2,
      label: 'Edit',
      onClick: () => {
        setVisibleBuildingUpdateDialog(true);
      },
    },
    {
      id: 3,
      label: 'Delete',
      onClick: () => {
        setVisibleBuildingDeleteDialog(true);
      },
    },
    {
      id: 4,
      label: 'Add Zone',
      onClick: () => {
        setVisibleZoneCreateDialog(true);
      },
    },
  ];

  return (
    <>
      <Box sx={{ padding: 3 }}>
        <Grid container rowSpacing={5} columnSpacing={3}>
          <Grid
            item
            xs={12}
            container
            spacing={2}
            alignItems={width < theme.breakpoints.values.md ? 'center' : 'flex-start'}
          >
            <Grid item sx={{ flex: 1 }}>
              <Breadcrumbs
                separator={
                  <Box sx={{ paddingX: 1.5 }}>
                    <ArrowForwardIos sx={{ fontSize: 10 }} />
                  </Box>
                }
              >
                <Link
                  component="button"
                  variant="subtitle2"
                  color="textSecondary"
                  onClick={() => {
                    navigate(`/${orgSlug}/${ROUTES.BUILDINGS}`);
                  }}
                >
                  Buildings
                </Link>

                {loading ? (
                  <CircularProgress size={16} sx={{ marginTop: 0.5 }} />
                ) : (
                  <Typography variant="subtitle2" color="primary">
                    {building ? getBuildingLocation(building) : 'All'}
                  </Typography>
                )}
              </Breadcrumbs>
            </Grid>

            {isAdmin && (
              <Grid item>
                <CrystalButtonPopup items={pageMenuItems} label="" />
              </Grid>
            )}

            {false && (
              <Grid item xs={12}>
                <MostActiveZones />
              </Grid>
            )}
          </Grid>

          <Grid item xs={12}>
            <ZonesTabInBuilding />

            {children}
          </Grid>
        </Grid>
      </Box>

      <BuildingUpdateDialog
        visible={visibleBuildingUpdateDialog}
        setVisible={setVisibleBuildingUpdateDialog}
        buildingId={Number(buildingId)}
      />

      <BuildingDeleteDialog
        visible={visibleBuildingDeleteDialog}
        setVisible={setVisibleBuildingDeleteDialog}
        buildingId={Number(buildingId)}
      />

      <ZoneCreateDialog
        visible={visibleZoneCreateDialog}
        setVisible={setVisibleZoneCreateDialog}
        buildingId={buildingId ? Number(buildingId) : undefined}
      />
    </>
  );
};
